import React, { useEffect, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { wrapper } from 'redux/store'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'

import Wrapper from 'components/Wrapper'
import { Button } from 'components/v2/Button'
import Filters from 'components/Search/Filters'
import { Column, Row } from 'components/v2/Grid'
import { PageTitle } from 'components/v2/PageTitle'
import PageTitleSEO from '../components/v2/PageTitleSEO'
import Loader from 'components/Search/Results/loader'
import NoResults from 'components/Search/Results/noResults'

import { addTo, resetFilters } from 'redux/modules/filters'
import { loadPsychos } from 'redux/modules/psychos'
import SKILLS from '../utils/seo/skills-online.json'
import SYMPTOMS from '../utils/seo/symptoms-online.json'
import SEO_DATA from '../components/v2/PageTitleSEO/seoData.json'
import { getSymptoms, loadSkills } from 'services/handleRequests'

import search from '../styles/search.scss'
import sched_pay from '../styles/sched_pay.scss'
import rc from 'react-dates/lib/css/_datepicker.css'
import PsychoCard from 'components/v2/Search/PsychoCard'

const DynamicFooter = dynamic(
  () => import('../components/shared/FooterStatic/FooterIntercomClickableHome'),
  {
    ssr: false
  }
)

const DynamicHeader = dynamic(
  () => import('../components/v2/Header').then(mod => mod.HeaderRendering),
  {
    ssr: false
  }
)

const BuscaOnline = ({ query }) => {
  const dispatch = useDispatch()
  const { query: queryClient } = useRouter()
  const { psychologists, filters } = useSelector(state => ({
    psychologists: state.psychos,
    filters: state.filters
  }))

  const symptoms = JSON.parse(SYMPTOMS)
  const skills = JSON.parse(SKILLS)

  const activeSymptoms =
    symptoms?.results?.filter(symptom => symptom?.slug === query?.symptom)[0] ?? null
  const activeSkills = skills?.results?.filter(skill => skill?.slug === query?.symptom)[0] ?? null
  const hasSeoData = SEO_DATA?.find(
    seo => seo?.slug === activeSymptoms?.description || activeSkills?.description
  )

  useEffect(() => {
    if (activeSymptoms) {
      dispatch(addTo('symptoms', activeSymptoms.description))
    }

    if (activeSkills) {
      dispatch(addTo('skills', activeSkills.skills[0].id))
    }
  }, [])

  useEffect(() => {
    dispatch(
      loadPsychos({
        ...filters
      })
    )
  }, [filters?.symptoms, filters?.skills])

  const onSearchPsychologists = useCallback(
    params =>
      dispatch(
        loadPsychos(
          {
            ...filters,
            ...params
          },
          false
        )
      ),
    [filters, dispatch]
  )

  const onPaginatePsychologists = useCallback(() => {
    dispatch(
      loadPsychos(
        {
          ...filters,
          page: psychologists?.next,
          already_appeared: (psychologists?.data || []).map(psychologist => psychologist?.id)
        },
        true
      )
    )
  }, [filters, dispatch, psychologists])

  const metaTags = {
    title: `Psicólogos Especializados em ${activeSymptoms?.description ||
      activeSkills?.description}`,
    description: `Psicólogos Online Especializados em ${activeSkills?.description ||
      activeSymptoms?.description} para videoconsultas. Vittude conecta você com profissionais recomendados. Confira a experiência, preço, opiniões e avaliações.`,
    hreflang: `${process.env.VITTUDE_OFFLINE_URL}/psicologos-online/${query?.symptom}`
  }

  return (
    <Wrapper
      noIndex
      metaTags={metaTags}
      cssFile={[search, sched_pay, rc]}
      coupon={queryClient?.coupon}
    >
      <Row position='absolute' width='100%' height='10vh' top='0' />
      <DynamicHeader />
      <Row display='block' id='search' background='#f8f8f8'>
        {hasSeoData ? (
          <PageTitleSEO seoDataFind={hasSeoData} />
        ) : (
          <PageTitle title={'Encontre seu psicólogo'} pt='42px' />
        )}
        <Filters onSearchPsychologists={onSearchPsychologists} />
        {psychologists?.data?.length === 0 && !psychologists?.loading && <NoResults />}
        {psychologists?.data?.length > 0 && (
          <Column
            maxWidth={['540px', '720px', '960px', '1140px']}
            width='100%'
            mx='auto'
            background='#f8f8f8'
          >
            {psychologists?.data?.map(psychologist => (
              <PsychoCard psychologist={psychologist} key={psychologist?.id} />
            ))}
          </Column>
        )}
        {psychologists?.loading && <Loader text='Aguarde enquanto carregamos resultados ...' />}
        {psychologists?.next && !psychologists?.loading && (
          <Column maxWidth={['540px', '720px', '960px', '1140px']} width='100%' mx='auto'>
            <Row mb='32px'>
              <Column flex='0 0 100%' maxWidth='100%' alignItems='center' justifyContent='center'>
                <Button
                  width='150px'
                  height='51px'
                  borderRadius='8px'
                  my='32px'
                  onClick={onPaginatePsychologists}
                  disabled={psychologists?.loading || !psychologists?.next}
                  opacity='1'
                  id='see-more-professionals'
                >
                  Ver mais
                </Button>
              </Column>
            </Row>
          </Column>
        )}
      </Row>
      <DynamicFooter />
    </Wrapper>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async ctx => {
  try {
    const { query } = ctx

    const [{ data: symptoms }, { data: skills }] = await Promise.all([getSymptoms(), loadSkills()])

    await store.dispatch(resetFilters())
    store.dispatch({ type: 'SYMPTOMS', payload: symptoms })
    store.dispatch({ type: 'SKILLS', payload: skills })

    return {
      props: {
        query
      }
    }
  } catch (error) {
    return {
      props: {}
    }
  }
})

export default BuscaOnline
